import React from "react";
import { motion } from "framer-motion";
import { Coins, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CategoryBadge, CapabilityBadge } from "@/components/AgentBadge";
import { cn } from "@/lib/utils";

const STATUS = {
  OPEN: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  ACTIVE: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
  COMPLETED: "text-slate-300 border-white/10 bg-white/5",
};

export const TaskCard = ({ task, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.35 }}
    className={cn("glass glass-hover rounded-2xl p-5", className)}
    data-testid={`task-card-${task.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-heading text-base font-bold text-white">{task.title}</h3>
      <span className={cn("shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider", STATUS[task.status])}>{task.status}</span>
    </div>
    <p className="mt-2 text-sm text-slate-400">{task.description}</p>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {task.caps.map((cap) => <CapabilityBadge key={cap} label={cap} />)}
    </div>
    <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
      <span className="inline-flex items-center gap-1.5 font-mono text-sm font-bold text-cyan-300">
        <Coins className="h-4 w-4" /> {task.reward} USDC
      </span>
      <button
        type="button"
        onClick={() => toast("Task preview", { description: "Task execution is a demo interaction in this MVP." })}
        data-testid={`view-task-${task.id}`}
        className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-cyan-400/40 hover:text-cyan-200"
      >
        View Task <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  </motion.div>
);

export const MarketplaceCard = ({ item, agent, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.35 }}
    className={cn("glass glass-hover flex flex-col rounded-2xl p-5", className)}
    data-testid={`marketplace-card-${item.id}`}
  >
    <div className="flex items-center justify-between">
      <CategoryBadge category={item.category} />
      <span className="font-mono text-xs text-slate-400">rep <span className="font-bold text-cyan-300">{item.reputation}</span></span>
    </div>
    <h3 className="mt-3 font-heading text-base font-bold text-white">{item.title}</h3>
    <p className="mt-1 text-xs text-slate-500">By @{item.agent}</p>
    <p className="mt-2 flex-1 text-sm text-slate-400">{item.description}</p>
    <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
      <span className="font-heading text-lg font-extrabold text-white">{item.price} <span className="text-sm font-semibold text-slate-400">USDC</span></span>
      <button
        type="button"
        onClick={() => toast("Hire agent", { description: "Payments are a visual demo in this MVP — USDC pricing shown as an example." })}
        data-testid={`hire-${item.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-400 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-[#050814] transition-all hover:bg-cyan-300 btn-glow"
      >
        <Coins className="h-3.5 w-3.5" /> Hire Agent
      </button>
    </div>
  </motion.div>
);

export default TaskCard;
